import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, ToggleLeft, ToggleRight, Puzzle } from 'lucide-react';

const EMPTY_ADDON = {
  name: '', description: '', price: 0, currency: 'MXN', billing_cycle: 'monthly',
  entitlement_key: '', additional_limit: 0, grants_boolean_entitlement: false,
  compatible_plans: [], compatible_verticals: [], sort_order: 0,
};

export default function Addons() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ADDON);
  const [saving, setSaving] = useState(false);

  const fetchAddons = async () => {
    try { const { data } = await api.get('/admin/addons'); setAddons(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAddons(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_ADDON); setShowForm(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, description: a.description || '', price: a.price, currency: a.currency,
      billing_cycle: a.billing_cycle, entitlement_key: a.entitlement_key || '',
      additional_limit: a.additional_limit, grants_boolean_entitlement: !!a.grants_boolean_entitlement,
      compatible_plans: a.compatible_plans || [],
      compatible_verticals: a.compatible_verticals || [], sort_order: a.sort_order });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/admin/addons/${editing.id}`, form); toast.success('Add-on actualizado'); }
      else { await api.post('/admin/addons', form); toast.success('Add-on creado'); }
      setShowForm(false); fetchAddons();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleToggle = async (a) => {
    const s = a.status === 'active' ? 'inactive' : 'active';
    try { await api.patch(`/admin/addons/${a.id}/status?status=${s}`); toast.success(`Add-on ${s}`); fetchAddons(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  if (showForm) return (
    <div data-testid="addon-form">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A]">{editing ? 'Editar Add-on' : 'Nuevo Add-on'}</h1>
        <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancelar</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre *</label>
            <Input data-testid="addon-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Extra Users" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Precio</label>
            <Input data-testid="addon-price" type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Clave de Entitlement</label>
            <Input data-testid="addon-entitlement-key" value={form.entitlement_key} onChange={e => setForm(f => ({ ...f, entitlement_key: e.target.value }))} placeholder="Ej: max_users o social_tiktok_enabled" />
            <p className="text-xs text-[#94A3B8] mt-1">La misma clave que usas en Plan Builder — un límite numérico (max_users) o una función on/off (social_tiktok_enabled).</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Qué hace este add-on al activarse</label>
            <div className="flex gap-2">
              <button type="button" data-testid="addon-mode-limit"
                onClick={() => setForm(f => ({ ...f, grants_boolean_entitlement: false }))}
                className={`flex-1 text-left px-3 py-2 rounded-md border text-sm ${!form.grants_boolean_entitlement ? 'border-[#0F172A] bg-[#F8FAFC]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                <div className="font-medium text-[#0F172A]">Suma un límite</div>
                <div className="text-xs text-[#94A3B8]">Ej: +2 usuarios, +500 mensajes al mes</div>
              </button>
              <button type="button" data-testid="addon-mode-boolean"
                onClick={() => setForm(f => ({ ...f, grants_boolean_entitlement: true }))}
                className={`flex-1 text-left px-3 py-2 rounded-md border text-sm ${form.grants_boolean_entitlement ? 'border-[#0F172A] bg-[#F8FAFC]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                <div className="font-medium text-[#0F172A]">Prende una función</div>
                <div className="text-xs text-[#94A3B8]">Ej: activa TikTok aunque el plan no lo incluya</div>
              </button>
            </div>
          </div>
          {!form.grants_boolean_entitlement && (
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Límite Adicional</label>
              <Input data-testid="addon-additional-limit" type="number" value={form.additional_limit} onChange={e => setForm(f => ({ ...f, additional_limit: parseInt(e.target.value) || 0 }))} />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Ciclo</label>
            <select className="w-full h-10 px-3 text-sm border border-[#E2E8F0] rounded-md" value={form.billing_cycle} onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
              <option value="monthly">Mensual</option><option value="annual">Anual</option><option value="one_time">Una vez</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Orden</label>
            <Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Descripción</label>
          <textarea className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-md resize-none" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0]">
          <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="addon-form-save" className="bg-[#0F172A] text-white hover:bg-[#1E293B]">
            {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Add-on')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="addons-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Add-ons</h1>
          <p className="text-sm text-[#64748B] mt-1">{addons.length} add-ons configurados</p>
        </div>
        <Button onClick={openCreate} data-testid="create-addon-btn" className="bg-[#0F172A] text-white hover:bg-[#1E293B]">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Add-on
        </Button>
      </div>
      {addons.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center">
          <Puzzle className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">No hay add-ons creados</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#F1F5F9] text-left">
              <th className="px-4 py-3 text-xs font-medium text-[#94A3B8]">Nombre</th>
              <th className="px-4 py-3 text-xs font-medium text-[#94A3B8] hidden sm:table-cell">Precio</th>
              <th className="px-4 py-3 text-xs font-medium text-[#94A3B8] hidden md:table-cell">Entitlement</th>
              <th className="px-4 py-3 text-xs font-medium text-[#94A3B8] hidden md:table-cell">Efecto</th>
              <th className="px-4 py-3 text-xs font-medium text-[#94A3B8]">Estado</th>
              <th className="px-4 py-3 text-xs font-medium text-[#94A3B8]">Acciones</th>
            </tr></thead>
            <tbody>
              {addons.map(a => (
                <tr key={a.id} className="border-b border-[#F8F9FA] hover:bg-[#F8F9FA]/50" data-testid={`addon-row-${a.id}`}>
                  <td className="px-4 py-3"><span className="font-medium text-[#0F172A]">{a.name}</span></td>
                  <td className="px-4 py-3 hidden sm:table-cell text-[#64748B]">${a.price} {a.currency}/{a.billing_cycle}</td>
                  <td className="px-4 py-3 hidden md:table-cell"><code className="text-xs bg-[#F1F5F9] px-1.5 py-0.5 rounded">{a.entitlement_key || '-'}</code></td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#64748B]">
                    {a.grants_boolean_entitlement
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Prende función</span>
                      : `+${a.additional_limit}`}
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>{a.status === 'active' ? 'Activo' : 'Inactivo'}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggle(a)}>
                        {a.status === 'active' ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
